/* A station's shouts: what listeners sent the DJ, what the filter made of it, and the DJ's answer. Operators can
   overrule the filter until the DJ has answered. Names are shown here only, never on air. */
import { For, Show, createResource, onCleanup } from 'solid-js';
import { api, fmtTime, Shout, Station } from '../api';
import { ErrorLine, StatusPill, useAction } from '../ui';

const TONE = { pending: 'warn', accepted: 'ok', rejected: 'bad', answered: 'ok' } as const;
const LABEL = { pending: 'Being checked', accepted: 'For the DJ', rejected: 'Refused', answered: 'Answered' };

export default function Shouts(props: { station: Station }) {
  const [rows, { refetch }] = createResource(() => props.station.id, (id) => api<Shout[]>(`/radio/stations/${id}/shouts`));
  const a = useAction();
  const t = setInterval(() => refetch(), 20_000);
  onCleanup(() => clearInterval(t));
  const verdict = (id: string, status: 'accepted' | 'rejected') => a.run(async () => {
    await api(`/radio/shouts/${id}/verdict`, { method: 'POST', json: { status } });
    refetch();
  });
  return (
    <>
      <p class="mb-3 text-sm text-ink-3">
        A filter checks each shout within a minute. The DJ answers the newest accepted shout of the last half hour in a
        link of its own, a song or two later, at most one every four minutes. Shouts are never shown publicly.
      </p>
      <ErrorLine error={a.error} />
      <Show when={rows()?.length} fallback={<p class="text-ink-3">{rows.loading ? 'Loading…' : 'No shouts yet.'}</p>}>
        <div class="overflow-x-auto">
          <table class="table">
            <thead><tr><th class="th">Sent</th><th class="th">Shout</th><th class="th">Filter</th><th class="th">DJ's answer</th><th /></tr></thead>
            <tbody>
              <For each={rows()}>
                {(sh) => (
                  <tr class="align-top">
                    <td class="whitespace-nowrap text-sm text-ink-3">{fmtTime(sh.created_at)}</td>
                    <td class="max-w-xs">
                      <span>{sh.text}</span>
                      <Show when={sh.name}><span class="block text-xs text-ink-3">from {sh.name}</span></Show>
                      <Show when={sh.clean && sh.clean !== sh.text}><span class="block text-xs text-ink-3">DJ sees: {sh.clean}</span></Show>
                    </td>
                    <td class="whitespace-nowrap">
                      <StatusPill tone={TONE[sh.status]}>{LABEL[sh.status]}</StatusPill>
                      <Show when={sh.reason}><span class="block text-xs text-ink-3">{sh.reason}</span></Show>
                      <Show when={sh.language && sh.language !== 'English'}><span class="block text-xs text-ink-3">{sh.language}</span></Show>
                    </td>
                    <td class="max-w-sm text-sm">
                      <Show when={sh.answer} fallback={<span class="text-ink-3">—</span>}>
                        {sh.answer}
                        <span class="block text-xs text-ink-3">{sh.answer_aired_at ? `aired ${fmtTime(sh.answer_aired_at)}` : 'planned'}</span>
                      </Show>
                    </td>
                    <td class="whitespace-nowrap text-right">
                      <Show when={sh.status === 'pending' || sh.status === 'accepted'}>
                        <button class="btn btn-ghost btn-xs text-error" disabled={a.busy()} onClick={() => verdict(sh.id, 'rejected')}>Refuse</button>
                      </Show>
                      <Show when={sh.status === 'rejected'}>
                        <button class="btn btn-ghost btn-xs" disabled={a.busy()} onClick={() => verdict(sh.id, 'accepted')}>Let through</button>
                      </Show>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </div>
      </Show>
    </>
  );
}
